/**
 * executiveDeptUnitsService — Unidades de valor + KPIs executivos (ex-Cockpit Diretoria).
 * SSOT de vínculos: user_organizations (department_id, reports_to_user_id, role_type).
 */

import { supabase } from '../lib/supabaseClient';
import { ANDON_PANEL_STATUSES, countsAsOpenAndon } from '../components/ops/exceptionLoop';
import type { OrgDepartment, OrgMember, RoleType } from './organizationService';
import { hydrateMembershipFromAssignments } from './organizationService';

const FALLBACK_SECTORS = [
  'Excelência Operacional',
  'Operações / Fábrica',
  'Logística e Supply Chain',
  'Qualidade / Laboratório',
] as const;

const FALLBACK_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'] as const;

const ROLE_RANK: Record<RoleType, number> = {
  director: 4,
  manager: 3,
  lead: 2,
  operational: 1,
};

function hashColor(str: string): string {
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

export interface DeptProject {
  id: string;
  name: string;
  status: string;
  progress: number;
}

export interface DeptUnit {
  department: OrgDepartment;
  manager: OrgMember | null;
  members: OrgMember[];
  projects: DeptProject[];
  actionCount: number;
  overdueCount: number;
  /** Quantidade de membros do departamento. Não é FTE contratual. */
  fteCount: number;
  isSynthetic: boolean;
}

export interface DeptQueryResult {
  units: DeptUnit[];
  isFallback: boolean;
}

export interface ExecutiveKpis {
  hoshinCount: number;
  projectsOverdue: number;
  andonTriggered: number;
  escalatedManager: number;
  criticalDirector: number;
}

interface MemberProfileInfo {
  full_name?: string | null;
  email?: string;
}

/** Carrega nomes/e-mails sem embed PostgREST (user_organizations → profiles não tem FK). */
async function loadMemberProfileMap(userIds: string[]): Promise<Map<string, MemberProfileInfo>> {
  const map = new Map<string, MemberProfileInfo>();
  if (userIds.length === 0) return map;

  const { data: userProfiles } = await supabase
    .from('user_profiles')
    .select('user_id, name, email, full_name')
    .in('user_id', userIds);
  for (const up of userProfiles ?? []) {
    map.set(up.user_id, {
      full_name: (up as { full_name?: string | null }).full_name || up.name || null,
      email: up.email ?? '',
    });
  }

  return map;
}

function mapRawMember(m: Record<string, unknown>, profileMap?: Map<string, MemberProfileInfo>): OrgMember {
  const profile = profileMap?.get(m.user_id as string);
  return {
    user_id: m.user_id as string,
    user_name: profile?.full_name ?? (m.user_id as string),
    user_email: profile?.email ?? '',
    role: (m.role as string | null) ?? null,
    role_type: ((m.role_type as RoleType) ?? 'operational'),
    role_id: (m.role_id as string | null) ?? null,
    role_name: null,
    department_id: (m.department_id as string | null) ?? null,
    department_name: null,
    reports_to_user_id: (m.reports_to_user_id as string | null) ?? null,
    reports_to_name: null,
    subordinate_count: 0,
    active_task_count: 0,
    late_task_count: 0,
  };
}

function hydrateMemberNames(members: OrgMember[]): OrgMember[] {
  const nameById = new Map(members.map((m) => [m.user_id, m.user_name]));
  return members.map((m) => ({
    ...m,
    reports_to_name: m.reports_to_user_id ? nameById.get(m.reports_to_user_id) ?? null : null,
  }));
}

/** Gestor do setor: cargo manager/director, ou nó hierárquico (reports_to) dentro do dept. */
export function resolveDeptManager(members: OrgMember[]): OrgMember | null {
  if (members.length === 0) return null;

  const byRank = [...members].sort((a, b) => ROLE_RANK[b.role_type] - ROLE_RANK[a.role_type]);
  const explicit = byRank.find((m) => m.role_type === 'director' || m.role_type === 'manager');
  if (explicit) return explicit;

  const withSubordinates = members.filter((m) =>
    members.some((other) => other.reports_to_user_id === m.user_id && other.user_id !== m.user_id),
  );
  if (withSubordinates.length > 0) {
    return [...withSubordinates].sort((a, b) => ROLE_RANK[b.role_type] - ROLE_RANK[a.role_type])[0];
  }

  const lead = byRank.find((m) => m.role_type === 'lead');
  if (lead) return lead;

  return null;
}

export async function fetchDeptUnitsWithFallback(orgId: string): Promise<DeptQueryResult> {
  const today = new Date().toISOString().split('T')[0];

  const [deptRes, memberRes] = await Promise.all([
    supabase.from('org_departments').select('*').eq('organization_id', orgId).order('name'),
    supabase.from('user_organizations').select('*').eq('organization_id', orgId),
  ]);

  let departments: OrgDepartment[] = (deptRes.data ?? []) as OrgDepartment[];
  const rawMembers = (memberRes.data ?? []) as Record<string, unknown>[];
  const profileMap = await loadMemberProfileMap(
    rawMembers.map((m) => m.user_id as string).filter(Boolean),
  );

  const userDeptMap = new Map<string, string>();
  for (const m of rawMembers) {
    const uid = m.user_id as string | null;
    const did = m.department_id as string | null;
    if (uid && did) userDeptMap.set(uid, did);
  }

  if (departments.length === 0) {
    const assignedDeptIds = [...new Set([...userDeptMap.values()])];
    if (assignedDeptIds.length > 0) {
      const { data: found } = await supabase.from('org_departments').select('*').in('id', assignedDeptIds);
      departments = (found ?? []) as OrgDepartment[];
    }
  }

  let isFallback = false;
  if (departments.length === 0) {
    isFallback = true;
    departments = FALLBACK_SECTORS.map((name, i) => ({
      id: `synthetic-${i}`,
      organization_id: orgId,
      name,
      color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      parent_department_id: null,
      created_at: new Date().toISOString(),
    }));
  }

  departments = departments.map((d) => ({
    ...d,
    color: d.color || hashColor(d.name),
  })) as OrgDepartment[];

  const mappedMembers = rawMembers.map((m) => mapRawMember(m, profileMap));
  const hydrateRows = mappedMembers.map((m) => ({
    user_id: m.user_id,
    department_id: m.department_id,
    role_id: m.role_id,
  }));
  await hydrateMembershipFromAssignments(orgId, hydrateRows);
  const hydratedByUser = new Map(hydrateRows.map((r) => [r.user_id, r]));
  for (const m of mappedMembers) {
    const h = hydratedByUser.get(m.user_id);
    if (!h) continue;
    if (h.department_id) m.department_id = h.department_id;
    if (h.role_id) m.role_id = h.role_id;
  }
  let allMembers = hydrateMemberNames(mappedMembers);

  if (isFallback) {
    const totalActions = (
      await supabase.from('actions').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).neq('status', 'cancelled')
    ).count ?? 0;
    const overdueTotal = (
      await supabase
        .from('actions')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .not('status', 'in', '(completed,cancelled)')
        .lt('due_date', today)
    ).count ?? 0;

    const units: DeptUnit[] = departments.map((dept, idx) => {
      const deptMembers = idx === 0 ? allMembers.filter((m) => !m.department_id) : [];
      return {
        department: dept,
        manager: resolveDeptManager(deptMembers),
        members: deptMembers,
        projects: [],
        actionCount: idx === 0 ? totalActions : 0,
        overdueCount: idx === 0 ? overdueTotal : 0,
        fteCount: deptMembers.length,
        isSynthetic: true,
      };
    });
    return { units, isFallback: true };
  }

  const [actionRes, pmRes, projRes] = await Promise.all([
    supabase
      .from('actions')
      .select('id, assigned_to, project_id, due_date, status, sla_status')
      .eq('organization_id', orgId)
      .neq('status', 'cancelled'),
    supabase.from('project_members').select('project_id, user_id'),
    supabase.from('projects').select('id, nome_projeto, visibility, expected_end_date').eq('organization_id', orgId).neq('visibility', 'archived'),
  ]);

  type RawAction = { id: string; assigned_to: string | null; project_id: string | null; due_date: string | null; status: string | null; sla_status: string | null };
  type RawPM = { project_id: string; user_id: string };
  type RawProject = { id: string; nome_projeto: string; visibility: string | null; expected_end_date: string | null };

  const rawActions = (actionRes.data ?? []) as RawAction[];
  const rawPMs = (pmRes.data ?? []) as RawPM[];
  const rawProjects = (projRes.data ?? []) as RawProject[];

  const projectDeptMap = new Map<string, Set<string>>();
  for (const pm of rawPMs) {
    const deptId = userDeptMap.get(pm.user_id);
    if (!deptId) continue;
    if (!projectDeptMap.has(pm.project_id)) projectDeptMap.set(pm.project_id, new Set());
    projectDeptMap.get(pm.project_id)!.add(deptId);
  }

  const actionCountByDept = new Map<string, number>();
  const overdueCountByDept = new Map<string, number>();
  const deptIdSet = new Set(departments.map((d) => d.id));

  function addToDept(deptId: string, isOverdue: boolean) {
    actionCountByDept.set(deptId, (actionCountByDept.get(deptId) ?? 0) + 1);
    if (isOverdue) overdueCountByDept.set(deptId, (overdueCountByDept.get(deptId) ?? 0) + 1);
  }

  for (const action of rawActions) {
    const isOverdue =
      Boolean(action.due_date) && action.due_date! < today && action.status !== 'completed';
    const assignedDept = action.assigned_to ? userDeptMap.get(action.assigned_to) : undefined;

    if (assignedDept && deptIdSet.has(assignedDept)) {
      addToDept(assignedDept, isOverdue);
    } else if (action.project_id) {
      const projectDepts = projectDeptMap.get(action.project_id);
      if (projectDepts) {
        for (const dId of projectDepts) {
          if (deptIdSet.has(dId)) addToDept(dId, isOverdue);
        }
      }
    }
  }

  const projectsByDept = new Map<string, DeptProject[]>();
  for (const proj of rawProjects) {
    const deptIds = projectDeptMap.get(proj.id);
    if (!deptIds) continue;
    for (const dId of deptIds) {
      if (!deptIdSet.has(dId)) continue;
      if (!projectsByDept.has(dId)) projectsByDept.set(dId, []);
      projectsByDept.get(dId)!.push({
        id: proj.id,
        name: proj.nome_projeto,
        status: proj.visibility ?? 'published',
        progress: 0,
      });
    }
  }

  const units: DeptUnit[] = departments.map((dept) => {
    const deptMembers = allMembers.filter((m) => m.department_id === dept.id);
    return {
      department: dept,
      manager: resolveDeptManager(deptMembers),
      members: deptMembers,
      projects: projectsByDept.get(dept.id) ?? [],
      actionCount: actionCountByDept.get(dept.id) ?? 0,
      overdueCount: overdueCountByDept.get(dept.id) ?? 0,
      fteCount: deptMembers.length,
      isSynthetic: false,
    };
  });

  return { units, isFallback: false };
}

export async function fetchExecutiveKpis(orgId: string, deptId?: string): Promise<ExecutiveKpis> {
  const today = new Date().toISOString().split('T')[0];
  let memberIds: string[] | undefined;

  if (deptId) {
    const { data } = await supabase
      .from('user_organizations')
      .select('user_id')
      .eq('organization_id', orgId)
      .eq('department_id', deptId);
    memberIds = ((data ?? []) as { user_id: string }[]).map((r) => r.user_id);
  }

  let hoshinCount = 0;
  if (deptId) {
    const { count, error } = await supabase
      .from('hoshin_tactical_goals')
      .select('id', { count: 'exact', head: true })
      .eq('department_id', deptId)
      .neq('status', 'completed');
    hoshinCount = error ? 0 : count ?? 0;
  } else {
    const { count, error } = await supabase
      .from('hoshin_objectives')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('status', 'active');
    hoshinCount = error ? 0 : count ?? 0;
  }

  let projectsOverdue = 0;
  if (deptId && memberIds && memberIds.length > 0) {
    const { data: pmRows } = await supabase.from('project_members').select('project_id').in('user_id', memberIds);
    const projectIds = [...new Set(((pmRows ?? []) as { project_id: string }[]).map((r) => r.project_id))];
    if (projectIds.length > 0) {
      const { count, error } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .in('id', projectIds)
        .lt('expected_end_date', today);
      projectsOverdue = error ? 0 : count ?? 0;
    }
  } else if (!deptId) {
    const { count, error } = await supabase
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .lt('expected_end_date', today);
    projectsOverdue = error ? 0 : count ?? 0;
  }

  let andonQuery = supabase
    .from('actions')
    .select('id, sla_status, assigned_to', { count: 'exact', head: false })
    .eq('organization_id', orgId)
    .not('status', 'in', '(completed,cancelled)')
    .in('sla_status', ['andon_triggered', 'escalated_manager', 'critical_director']);

  const { data: andonRows, error: andonError } = await andonQuery;
  if (andonError) {
    return { hoshinCount, projectsOverdue, andonTriggered: 0, escalatedManager: 0, criticalDirector: 0 };
  }

  let rows = (andonRows ?? []) as { sla_status: string; assigned_to: string | null }[];
  if (deptId && memberIds && memberIds.length > 0) {
    const idSet = new Set(memberIds);
    rows = rows.filter((r) => r.assigned_to && idSet.has(r.assigned_to));
  }

  return {
    hoshinCount,
    projectsOverdue,
    andonTriggered: rows.filter((r) => r.sla_status === 'andon_triggered').length,
    escalatedManager: rows.filter((r) => r.sla_status === 'escalated_manager').length,
    criticalDirector: rows.filter((r) => r.sla_status === 'critical_director').length,
  };
}

export interface OpenAndonAction {
  id: string;
  title: string;
  sla_status: string;
  assigned_to: string | null;
}

/** Lista as mesmas ações que compõem o número Andons abertos. Não cria entidade Andon. */
export async function listOpenAndonActions(orgId: string, deptId?: string): Promise<OpenAndonAction[]> {
  let memberIds: string[] | undefined;
  if (deptId) {
    const { data } = await supabase
      .from('user_organizations')
      .select('user_id')
      .eq('organization_id', orgId)
      .eq('department_id', deptId);
    memberIds = ((data ?? []) as { user_id: string }[]).map((row) => row.user_id);
  }

  const { data, error } = await supabase
    .from('actions')
    .select('id, title, sla_status, assigned_to')
    .eq('organization_id', orgId)
    .not('status', 'in', '(completed,cancelled)')
    .in('sla_status', [...ANDON_PANEL_STATUSES]);

  if (error || !data) return [];

  let rows = data as OpenAndonAction[];
  rows = rows.filter((row) => countsAsOpenAndon(row.sla_status));
  if (deptId && memberIds && memberIds.length > 0) {
    const idSet = new Set(memberIds);
    rows = rows.filter((row) => row.assigned_to && idSet.has(row.assigned_to));
  }
  return rows;
}
